import React, {useEffect, useState} from "react";
import {NavigateFunction, useNavigate} from "react-router-dom";
import EditIcon from '@mui/icons-material/Edit';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowId,
  GridSortModel
} from '@mui/x-data-grid';
import {Chip, FormControl, InputLabel, Link, MenuItem, Select, SelectChangeEvent, TextField} from "@mui/material";
import IPage from "../../types/page.type";
import {Box, Breadcrumbs, Button, Grid, Typography} from "@mui/joy";
import {Experimental_CssVarsProvider as CssVarsProvider} from "@mui/material/styles";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import SearchIcon from "@mui/icons-material/Search";
import moment, {Moment} from "moment/moment";
import {deleteById, getMyLiqiudations} from "../../services/liquidation.service";
import ILiquidation from "../../types/liquidation.type";
import {LiquidationStatusColors} from "../../types/enumeration/LiquidationStatusColors";
import {LiquidationStatusNames} from "../../types/enumeration/LiquidationStatusNames";
import {LiquidationStatus} from "../../types/enumeration/LiquidationStatus";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ConfirmationModal from "../utils/ConfirmationModal";
import showNotification from "../utils/Notify";

const MyLiquidationsList: React.FC = () => {
  let navigate: NavigateFunction = useNavigate();
  const [liquidationsPage, setLiquidationsPage] = useState<IPage>({ content: [], totalElements: 0});
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState("id");
  const [sortDirection, setSortDirection] = useState<any>("asc");
  const [idFilter, setIdFilter] = useState( "");
  const [periodFilter, setPeriodFilter] = useState( "");
  const [statusFilter, setStatusFilter] = useState( "-1");
  const [selectedLiquidationId, setSelectedLiquidationId] = React.useState<any>();
  const [confirmationDeleteModalOpen, setConfirmationDeleteModalOpen] = React.useState(false);

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  const columns: GridColDef<ILiquidation>[] = [
    {
      field: 'id',
      headerName: 'ID',
      headerAlign: 'center',
      align: 'center',
      maxWidth: 60,
      flex: 1
    },
    {
      field: 'imputationPeriod',
      headerName: 'Periodo',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.row.imputationPeriod) {
          return <span>-</span>;
        }

        const [year, month] = params.row.imputationPeriod as [number, number];
        return <span>{`${String(month).padStart(2, '0')}-${year}`}</span>;
      }
    },
    {
      field: 'status',
      headerName: 'Estado',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <Chip color={LiquidationStatusColors[LiquidationStatus[params.row.status as keyof typeof LiquidationStatus]]}
                     label={LiquidationStatusNames[LiquidationStatus[params.row.status as keyof typeof LiquidationStatus]]}
                     sx={{width: 160, fontWeight: 'bold'}}/>;
      }
    },
    {
      field: 'totalAmount',
      headerName: 'Monto',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >${`${(params.row.purchases?.reduce((sum: any, purchase: { totalAmount: any; }) => sum + purchase.totalAmount, 0) ?? 0).toFixed(2)}`}</span>;
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      cellClassName: 'actions',
      getActions: (params) => {
        const { id, status } = params.row;
        const actions = [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />
        ];
        if (status === LiquidationStatus.IN_PROGRESS) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteForeverIcon />}
              label="Delete"
              className="textPrimary"
              onClick={handleDeleteClick(id)}
              color="inherit"
            />
          )
        }
        return actions;
      },
    },
  ];

  const handleAddClick = () => {
    navigate('create');
  };

  const handleEditClick = (id: GridRowId) => () => {
    navigate('edit/'+id)
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setSelectedLiquidationId(id);
    setConfirmationDeleteModalOpen(true);
  };

  const handleConfirmationDeleteModalClose = () => {
    setSelectedLiquidationId(null);
    setConfirmationDeleteModalOpen(false);
  }

  const onConfirmDelete = () => {
    deleteById(selectedLiquidationId)
      .then(() => {
        showNotification({type: 'success', message: 'Liquidacion eliminada correctamente.'});
        handleConfirmationDeleteModalClose();
        fetchLiquidations()
      })
      .catch(error => {
        showNotification({type: 'error', message: error.response.data.message});
      });
  };

  const handleChangeIdFilter = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setIdFilter(event.target.value)
  };

  const handleChangeImputationPeriod = (value: Moment | null) => {
    if (!value) {
      setPeriodFilter("");
      return;
    }
    setPeriodFilter(moment(value).format("YYYY-MM"));
  };

  const handleChangeStatus = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value)
  };

  const handlePaginationModelChange = (newPaginationModel: GridPaginationModel) => {
    setPaginationModel(newPaginationModel);
    fetchLiquidations(newPaginationModel.page);
  };

  const handleSortModelChange = React.useCallback((sortModel: GridSortModel) => {
    let field = sortModel[0] ? sortModel[0].field : "id";
    let order = sortModel[0] ? sortModel[0].sort : "asc";
    fetchLiquidations(paginationModel.page, paginationModel.pageSize, field, order);
    setSortColumn(field);
    setSortDirection(order);
  }, []);

  const fetchLiquidations = async (page: number = paginationModel.page, size: number = paginationModel.pageSize, sort: string = sortColumn, order: any = sortDirection) => {
    setLoading(true);
    type Param = {[key: string] : string}
    const params: Param = {}
    params.page = String(page);
    params.size = String(size);
    params.sort = sort;
    params.order = order;

    if (idFilter) {
      params.id = idFilter;
    }

    if (periodFilter) {
      params.periodFilter = periodFilter;
    }

    if (statusFilter !== '-1') {
      params.status = statusFilter;
    }

    getMyLiqiudations(params).then( (response) => {
      setLiquidationsPage(response.data)
      setLoading(false);
    })
  };

  useEffect(() => {
    fetchLiquidations();
  }, []);

  return (
    <Box
      sx={{
        px: { xs: 2},
        pt: { xs: 'calc(12px + var(--Header-height))' },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100dvh',
        overflow: "auto"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', px: { xs: 1} }}>
            <Breadcrumbs
              size="sm"
              aria-label="breadcrumbs"
              separator={<ChevronRightRoundedIcon/>}
              sx={{ pl: 0 }}
            >
              <Typography level="h4" component="h1">
                <CurrencyExchangeIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} />
              </Typography>
              <Typography level="h4" component="h1">
                Mis Liquidaciones
              </Typography>
              <Typography level="h4" component="h1">
                <Link
                  underline={"none"}
                  onClick={() => handleAddClick()}
                  sx={{ cursor: 'pointer' }}
                >
                  <AddCircleIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} /> Crear Nuevo
                </Link>
              </Typography>
            </Breadcrumbs>
          </Box>

          <ConfirmationModal
            open={confirmationDeleteModalOpen}
            onConfirm={onConfirmDelete}
            onCancel={handleConfirmationDeleteModalClose}
            title="Confirmacion"
            message="Esta seguro de eliminar esta Liquidacion?"
            isDeleteAction={true}
          />

          <Grid
            container
            rowSpacing={2}
            columnSpacing={1}
            paddingLeft={0.5}
            paddingRight={0.5}
            marginBlock={1}
            marginInline={0}
            sx={{ width: '100%' }}
          >
            <Grid xl={1} md={2} sm={2} xs={12}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  id="idFilter"
                  label="ID"
                  name="idFilter"
                  variant="filled"
                  type="text"
                  onChange={handleChangeIdFilter}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') {
                      fetchLiquidations();
                      ev.preventDefault();
                    }
                  }}
                  slotProps={{ inputLabel: {shrink: true}}}
                />
              </CssVarsProvider>
            </Grid>
            <Grid xl={6} md={10} sm={10} xs={12}>
              <CssVarsProvider>
                <LocalizationProvider dateAdapter={AdapterMoment} localeText={{ clearButtonLabel: "Limpiar" }}>
                  <DatePicker
                    views={["year", "month"]}
                    name="start"
                    label="Periodo"
                    format="MM-YYYY"
                    onChange={handleChangeImputationPeriod}
                    slotProps={{
                      actionBar: {
                        actions: ["clear", "accept"],
                      },
                      textField: {
                        variant: 'filled',
                        fullWidth: true,
                        InputLabelProps: {
                          shrink:  true
                        }
                      }
                    }} />
                </LocalizationProvider>
              </CssVarsProvider>
            </Grid>

            <Grid xl={3} md={9} sm={9} xs={12}>
              <CssVarsProvider>
                <FormControl variant="filled" fullWidth>
                  <InputLabel id="type-label" shrink>Estado</InputLabel>
                  <Select
                    labelId="type-label"
                    id="type"
                    name="type"
                    label="type"
                    value={statusFilter}
                    onChange={handleChangeStatus}
                  >
                    <MenuItem key="-1" value="-1">Todos</MenuItem>
                    {Object
                      .keys(LiquidationStatus)
                      .filter(key => [LiquidationStatus.SUBMITTED.toString(), LiquidationStatus.IN_PROGRESS.toString(), LiquidationStatus.TO_FIX.toString()]
                        .includes(key))
                      .map((key) => (
                        <MenuItem key={key} value={LiquidationStatus[key as keyof typeof LiquidationStatus]}>
                          {LiquidationStatusNames[LiquidationStatus[key as keyof typeof LiquidationStatus]]}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </CssVarsProvider>
            </Grid>
            <Grid xl={2} md={3} sm={3} xs={12}>
              <Button
                variant="outlined"
                fullWidth
                style={{height: 56}}
                endDecorator={<SearchIcon />}
                onClick={() => fetchLiquidations()}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ width: '100%', height: 632, typography: 'body1' }} ml={1} mt={0} pr={2}>
            <CssVarsProvider>
              <DataGrid
                rows={liquidationsPage.content}
                columns={columns}
                paginationModel={paginationModel}
                rowCount={liquidationsPage.totalElements}
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
                paginationMode="server"
                onPaginationModelChange={handlePaginationModelChange}
                loading={loading}
                disableRowSelectionOnClick
              />
            </CssVarsProvider>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MyLiquidationsList;